import { $, $$, isMobile } from '@utils';
import datalayerPushGeneric from '@utils/DataLayerPushGeneric';

//# Collapsable #//
/**
 * Use:
 *  Import file and exec "Collapsable.init()"
 */
const SELECTOR_CLICK_OUTSIDE = '[data-au-accordion-close-on-click-outside]',
  SELECTOR_ACCORDION = '[data-au-accordion]',
  SELECTOR = '[data-au-collapsable]',
  SELECTOR_CONTENT = '[data-au-collapsable-content]',
  SELECTOR_ICON = '[data-au-collapsable-icon]',
  ICON_COLLAPSABLE_CLOSED = 'data-au-icon-closed',
  ICON_COLLAPSABLE_OPENED = 'data-au-icon-opened',
  CLASS = 'au-closed',
  KEYCODE = 32; // Space key

function init(selector = SELECTOR) {
  if (!$(selector)) return;
  const triggers = $$(selector);

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      toggleExpand.call(this, trigger);
    });
    trigger.addEventListener('keydown', (e) => handleKeydown.call(this, e, trigger));

    if (trigger.hasAttribute('data-au-collapse-desktop') || trigger.hasAttribute('data-au-prevent-focus')) {
      isMobile.addEventListener('change', () => {
        collapseOnChangeMediaQuery(trigger);
      });
    }

    if (trigger.hasAttribute('data-au-collapse-mobile')) {
      collapseOnChangeMediaQuery(trigger);
    }

    if (isMobile.matches && trigger.hasAttribute('data-au-expand-mobile')) {
      expand(trigger);
    }

    addIcon(trigger);
  });

  // remove focus of content inside closed dropdowns and hide its content on load
  $$(`${SELECTOR_CONTENT}.au-closed`).forEach((el) => {
    removeFocus(el);
    hideAllContentOnInit(el);
  });

  dataLayerInsideCollapsable();

  if (`${SELECTOR_CLICK_OUTSIDE}`) {
    closeAllWhenInteractOutside(SELECTOR_CLICK_OUTSIDE);
  }

  closeCollapsable();
}

/**
 * Use:
 * data-au-collapse-mobile: To collapse in mobile and expand on desktop
 * data-au-collapse-desktop: To collapse in desktop and expand on mobile
 * data-au-prevent-focus: To prevent tabulation on desktop
 */
function collapseOnChangeMediaQuery(trigger) {
  const hasMobileAttr = trigger.hasAttribute('data-au-collapse-mobile');
  const hasDesktopAttr = trigger.hasAttribute('data-au-collapse-desktop');

  if ((hasMobileAttr && isMobile.matches) || (hasDesktopAttr && !isMobile.matches)) {
    collapse(trigger);
  } else if (hasMobileAttr || hasDesktopAttr) {
    expand(trigger);
  }

  if (trigger.hasAttribute('data-au-prevent-focus') && !isMobile.matches) {
    trigger.setAttribute('tabindex', '-1');
  } else {
    trigger.removeAttribute('tabindex');
  }

  addIcon(trigger);
  toggleIcon(trigger);
}

function handleKeydown(e, item) {
  if (e.keyCode === KEYCODE) {
    toggleExpand(item);
    e.stopPropagation();
    e.preventDefault();
  }
}

function toggleExpand(item) {
  item.classList.toggle(CLASS);

  const collapsableAccordion = item.closest(SELECTOR_ACCORDION);
  const isOpened = item.classList.contains(CLASS);

  if (collapsableAccordion && isOpened) {
    collapse(item);
  } else if (collapsableAccordion) {
    collapseAll(item);
    expand(item);
  } else if (isOpened) {
    collapse(item);
  } else {
    expand(item);
  }

  toggleIcon(item);
}

function collapsableContent(item) {
  return document.querySelectorAll(`#${item.getAttribute('aria-controls')}`);
}

function expand(item) {
  item.classList.remove(CLASS);
  item.setAttribute('aria-expanded', 'true');

  collapsableContent(item).forEach((i) => {
    i.classList.remove(CLASS);
    i.classList.remove('au-hide');

    addFocus(i);
  });
}

function collapse(item) {
  item.classList.add(CLASS);
  item.setAttribute('aria-expanded', 'false');

  collapsableContent(item).forEach((i) => {
    i.classList.add(CLASS);

    removeFocus(i);
  });
}

function collapseAll(item, selector = SELECTOR, selector_accordion = SELECTOR_ACCORDION) {
  const accordion = item.closest(selector_accordion);

  accordion.querySelectorAll(selector).forEach((collapsable) => {
    // Solo cerrar colapsables que están directamente dentro del accordion
    const isDirectChild = collapsable.parentElement.closest(selector_accordion) === accordion;
    if (isDirectChild) {
      collapse(collapsable);
    }
  });
}

// hide all closed contents on init to avoid see transition when we close it on load
function hideAllContentOnInit(item) {
  item.classList.add('au-hide');

  setTimeout(() => {
    item.classList.remove('au-hide');
  }, 400);
}

function setOpenedIcon(item, opened) {
  const collapsableIcon = item.querySelector(SELECTOR_ICON);

  if (opened) {
    collapsableIcon.innerHTML = openedIconName(item);
  } else {
    collapsableIcon.innerHTML = closedIconName(item);
  }
}

function closedIconName(item) {
  return item.getAttribute(ICON_COLLAPSABLE_CLOSED);
}

function openedIconName(item) {
  return item.getAttribute(ICON_COLLAPSABLE_OPENED);
}

function addIcon(item) {
  if (!item.getAttribute(ICON_COLLAPSABLE_CLOSED) || !item.hasAttribute(ICON_COLLAPSABLE_OPENED)) return;

  if (item.querySelector(SELECTOR_ICON) && item.hasAttribute('data-au-collapse-mobile') && !isMobile.matches) {
    item.querySelector(SELECTOR_ICON).remove();
  }
  if ((item.hasAttribute('data-au-collapse-mobile') && !isMobile.matches) || item.querySelector(SELECTOR_ICON)) {
    return;
  }
  let icon_collapsable;

  if (item.classList.contains(CLASS)) {
    icon_collapsable = closedIconName(item);
  } else {
    icon_collapsable = openedIconName(item);
  }

  const collapsableIcon = document.createElement('span');
  collapsableIcon.classList.add('material-symbols-rounded');
  collapsableIcon.setAttribute('aria-hidden', 'true');
  collapsableIcon.setAttribute('data-au-collapsable-icon', '');
  collapsableIcon.setAttribute('data-au-icon', '');
  const collapsableName = document.createTextNode(icon_collapsable);
  collapsableIcon.appendChild(collapsableName);

  if (item.dataset.auIconToOpenOnRight !== '') {
    item.prepend(collapsableIcon);
  } else {
    item.append(collapsableIcon);
  }
}

function toggleIcon(item) {
  const collapsableIcon = item.querySelector(SELECTOR_ICON);
  if (!collapsableIcon) return;
  const collapsableAccordion = item.closest(SELECTOR_ACCORDION);
  const isOpened = item.classList.contains(CLASS);

  if (collapsableAccordion && isOpened) {
    collapsableIcon.innerHTML = closedIconName(item);
    setOpenedIcon(item, false);
  } else if (collapsableAccordion) {
    collapsableAccordion.querySelectorAll(SELECTOR).forEach((collapsable) => {
      const icon = collapsable.querySelector(SELECTOR_ICON);
      if (icon) {
        icon.innerHTML = closedIconName(collapsable);
      }
    });
    setOpenedIcon(item, true);
  } else if (isOpened) {
    setOpenedIcon(item, false);
  } else {
    setOpenedIcon(item, true);
  }
}

/**
 * Returns back a NodeList of focusable elements
 * that exist within the passed parent HTMLElement, or
 * an empty array if no parent passed.
 *
 * @param {HTMLElement} parent HTML element
 * @returns {(NodeList|Array)} The focusable elements that we can find
 */
function getAllFocusableElements(parent) {
  if (!parent) {
    // console.warn('You need to pass a parent HTMLElement');
    return []; // Return array so length queries will work
  }

  return parent.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)',
  );
}

/**
 * Remove focus of elements passed on item param
 *
 * @param {HTMLElement} item HTML elements to remove focus
 */
function removeFocus(item) {
  getAllFocusableElements(item).forEach((focusable) => {
    focusable.setAttribute('tabindex', '-1');
  });
}

/**
 * Add focus of elements passed on item param
 *
 * @param {HTMLElement} item HTML elements to remove focus
 */
function addFocus(item) {
  getAllFocusableElements(item).forEach((focusable) => {
    if (!focusable.hasAttribute('data-au-prevent-focus') && focusable.ariaHidden !== 'true') {
      focusable.removeAttribute('tabindex');
    }
  });
}

function dataLayerInsideCollapsable() {
  $$('[data-au-collapsable-content]').forEach((content) => {
    // if content has this attribute, datalayer will be managed by the custom script of the component
    if (content.querySelector('[data-au-collaspsable-content-custom]')) return;

    content.querySelectorAll('a').forEach((elem) => {
      if (elem.dataset.auDatalayerCustom === '' || elem.dataset.auDatalayer === 'link') return false;

      let titleText = elem
        .closest('[data-au-collapsable-parent]')
        .querySelector('[data-au-collapsable-title-text]').innerText;
      titleText = titleText.length > 100 ? `${titleText.substring(0, 100)}...` : titleText;

      let clickText = elem.querySelector('span:not([data-au-icon])')
        ? elem.querySelector('span:not([data-au-icon])').innerText
        : elem.innerText;

      clickText = clickText.length > 100 ? `${clickText.substring(0, 100)}...` : clickText;

      if (elem.href.indexOf(window.location.origin) > -1) {
        elem.addEventListener('click', (e) =>
          datalayerPushGeneric(e, 'navigation', 'clickEnlace', titleText, clickText),
        );
      }
    });
  });
}

function closeAllWhenInteractOutside(SELECTOR_CLICK_OUTSIDE) {
  window.addEventListener('keyup', (e) => {
    closeAllCollapsables(e, SELECTOR_CLICK_OUTSIDE);
  });

  document.addEventListener('click', (e) => {
    closeAllCollapsables(e, SELECTOR_CLICK_OUTSIDE);
  });
}

function closeAllCollapsables(e, SELECTOR_CLICK_OUTSIDE) {
  const code = e.keyCode ? e.keyCode : e.which,
    selectorClickOutside = e.target.closest(SELECTOR_CLICK_OUTSIDE);

  if ((selectorClickOutside && code === 27) || selectorClickOutside === null) {
    $$(`${SELECTOR_CLICK_OUTSIDE}`).forEach((el) => {
      collapseAll(el);

      el.querySelectorAll(SELECTOR).forEach((selector) => {
        setOpenedIcon(selector, false);
      });
    });
  }
}

function closeCollapsable() {
  $$('[data-au-collapsable-close]').forEach((close) =>
    close.addEventListener('click', (e) =>
      collapse(e.target.closest('[data-au-collapsable-parent]').querySelector('[data-au-collapsable]')),
    ),
  );
}

export default { init };
